import { useState, useRef, useEffect } from "react";
import { Play, Lightning, Check, Target, Barbell, X, CaretDown, PaperPlaneTilt } from "@phosphor-icons/react";
import { T } from "../../themes";
import { TODAY_ISO, fmtDate, fmtDuration } from "../../constants";
import { llmFetch, LLM_MODEL_LIGHT, cleanAIResponse, AI_SAFETY_LAW, WORKOUT_SAFETY_LAW, INJURY_LAW, useChatCap } from "../../llm";
import {
  calcRecoveryMap, MuscleVolumeChart,
  calcWorkoutStreak,
  completedSets, totalVolume,
  ComicCard, SectionLabel, Btn, Spinner
} from "../../workoutHelpers";
import AIText from "../../components/AIText";
import AppHeader from "../../components/AppHeader";

export default function DashboardScreen({sessions,library,unit,profile,prMap,onStartWorkout,onStartRoutine,onViewHistory,routines,programmes,darkMode,setDarkMode,hcData,persona,buildWorkoutChatContext,sendChatMessage,getLLMUrl,userId,effectiveIsPremium}){
  const todaySessions=sessions.filter(s=>s.date===TODAY_ISO());
  const recent=[...sessions].sort((a,b)=>b.date<a.date?-1:1).slice(0,5);
  const sevenAgo=new Date(); sevenAgo.setDate(sevenAgo.getDate()-7);
  const weekSess=sessions.filter(s=>new Date(s.date)>=sevenAgo);

  // V26 FIX 23: Deload detection dismissal state
  const [deloadDismissed, setDeloadDismissed] = useState(false);

  // Switch session modal
  const [showSwitchModal, setShowSwitchModal] = useState(false);

  // ── Collapsible states ────────────────────────────────────────────────────
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [sessionsOpen, setSessionsOpen] = useState(true);
  const [moreOpen, setMoreOpen] = useState(false);

  // ── Workout chat state ────────────────────────────────────────────────────
  const workoutChatCap = useChatCap(`lift_chat_daily_${userId}`, 15);
  const [workoutChatHistory, setWorkoutChatHistory] = useState([]);
  const [workoutChatLoading, setWorkoutChatLoading] = useState(false);
  const [workoutChatError, setWorkoutChatError] = useState(null);
  const chatMsgsRef = useRef(null);
  useEffect(() => { if (chatMsgsRef.current) chatMsgsRef.current.scrollTop = chatMsgsRef.current.scrollHeight; }, [workoutChatHistory, workoutChatLoading]);

  async function handleWorkoutChatSend(userMessage) {
    if (!effectiveIsPremium || workoutChatCap.isExhausted || !userMessage || !buildWorkoutChatContext || !sendChatMessage) return;
    setWorkoutChatLoading(true); setWorkoutChatError(null);
    try {
      const systemPrompt = buildWorkoutChatContext();
      const reply = await sendChatMessage({ systemPrompt, history: workoutChatHistory, userMessage, getLLMUrl });
      setWorkoutChatHistory(prev => [...prev, { role: "user", content: userMessage }, { role: "assistant", content: reply }]);
      workoutChatCap.increment();
    } catch (err) { setWorkoutChatError(err); }
    finally { setWorkoutChatLoading(false); }
  }

  // ── Workout summary state (AI 1-liner per routine) ───────────────────────
  const [workoutSummaries, setWorkoutSummaries] = useState(() => {
    try { return JSON.parse(localStorage.getItem('lf_workout_summaries') || '{}'); } catch(_){ return {}; }
  });
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryFailed, setSummaryFailed] = useState({});

  // ── Part 4: AI workout suggestion state ──────────────────────────────────
  const [aiSuggestion, setAiSuggestion] = useState(()=>{
    try { return JSON.parse(localStorage.getItem("lf_last_suggestion")||"null"); } catch(_){ return null; }
  });
  const [aiLoading, setAiLoading]       = useState(false);
  const [aiError,   setAiError]         = useState("");
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);

  const WORKOUT_VOICE = {
    Motivational: "VOICE: You are a high-energy coach. Frame the session as an OPPORTUNITY. Reference what muscle groups are READY and what gains are available. Use RPE data — if recent sets were RPE 6-7, tell them there is room to push harder. End with a specific challenge.",
    Balanced: "VOICE: You are a clinical trainer. Explain WHY this session makes sense based on recovery data. Reference RPE to justify load recommendations. Practical and specific.",
    Critical: "VOICE: You are a drill sergeant. No fluff. State which muscles are ready, which need rest, and exactly what to do. Reference RPE directly. If they went easy last time (RPE under 7), say so. One command at the end.",
  };

  // Pre-generation modal state
  const [showWorkoutModal, setShowWorkoutModal] = useState(false);
  const [workoutFocus, setWorkoutFocus] = useState('');
  const [workoutDuration, setWorkoutDuration] = useState(45);

  async function suggestNextWorkout(focusOverride = '', durationOverride = 45) {
    if (!effectiveIsPremium) return;
    if (sessions.length < 3) return;
    setAiLoading(true); setAiError("");
    try {
      const recovMap = calcRecoveryMap(sessions, library);
      const recovLines = Object.entries(recovMap)
        .map(([m,d])=>`  ${m}: ${d.status==="untrained"?"Never trained":d.pct+"%"} (${d.daysSince===null?"—":d.daysSince+"d ago"}, ${d.sets} sets last)`)
        .join("\n");
      const last14 = [...sessions].sort((a,b)=>b.date<a.date?-1:1).slice(0,14);
      const histLines = last14.map(s=>{
        const exLines = (s.exercises||[]).map(ex=>{
          const doneSets=(ex.sets||[]).filter(st=>st.done);
          const rpeSets=doneSets.filter(st=>st.rpe&&st.rpe!=="");
          const avgRpe=rpeSets.length>0?(rpeSets.reduce((sum,st)=>sum+parseFloat(st.rpe),0)/rpeSets.length).toFixed(1):null;
          return `${ex.name}${avgRpe?` (RPE ${avgRpe})`:""}`;
        }).join(", ");
        return `  ${s.date} | ${s.name||"Session"} | ${fmtDuration(s.duration||0)} | ${exLines}`;
      }).join("\n");
      const activeProg = (programmes||[]).find(p=>p.active);
      const dayName = new Date().toLocaleDateString("en-GB",{weekday:"long"});
      const focusContext = focusOverride
        ? `USER_FOCUS: The user wants to train ${focusOverride} today. Prioritise exercises for this muscle group or movement pattern.`
        : `USER_FOCUS: No preference specified — use the recovery map to decide the best session.`;
      const durationContext = `SESSION_DURATION: The user has ${durationOverride} minutes. A 30-minute session = 3-4 exercises, 3 sets each. A 45-minute session = 4-5 exercises, 3-4 sets each. A 60-minute session = 5-6 exercises, 3-4 sets each. A 75-90 minute session = 6-8 exercises, 4 sets each. Match the volume to the time.`;
      const safeGoal = (profile?.physique_goal||"").replace(/[<>{}\[\]]/g,"").replace(/system:/gi,"").trim().slice(0,500);
      const physiqueContext = safeGoal
        ? `PHYSIQUE_GOAL: The user's target physique is "${safeGoal}" and their target weight is ${profile.target_weight||"not set"}kg. Tailor the workout to support this goal — exercise selection, volume, and rep ranges should reflect what builds this type of physique.`
        : '';
      const raw = await llmFetch({
        model: LLM_MODEL_LIGHT,
        max_tokens: 600,
        temperature: 0.3,
        messages: [
          { role:"system", content:
            `${WORKOUT_VOICE[persona] || WORKOUT_VOICE.Balanced} ` +
            "You are an expert strength coach with 20 years experience programming for natural athletes. "+
            "You have access to the athlete's recent training history and muscle recovery status. "+
            "Recommend the optimal next training session. Be specific: name the muscle groups to target, "+
            "suggest 4-6 exercises by name, recommend sets and reps for each. "+
            "Base your recommendation on actual recovery data, not generic templates. "+
            "If the athlete is clearly overtrained in a muscle group, say so explicitly. "+
            "Return ONLY a raw JSON object. No markdown, no preamble. "+
            'Schema: {"focus":string,"rationale":string,"exercises":[{"name":string,"sets":number,"reps":string,"notes":string}],"warnings":string[]} '+
            AI_SAFETY_LAW + " " +
            WORKOUT_SAFETY_LAW + " " +
            INJURY_LAW + " " +
            "PHYSIQUE_LAW: Always reference the user's physique goal when explaining why specific exercises were chosen. Plain English only — no science jargon."
          },
          { role:"user", content:
            `${focusContext}\n${durationContext}\n${physiqueContext}\n\n`+
            `TRAINING HISTORY (last 14 sessions):\n${histLines||"  No sessions yet"}\n\n`+
            `MUSCLE RECOVERY STATUS:\n${recovLines}\n\n`+
            `ACTIVE PROGRAMME: ${activeProg?activeProg.name:"None"}\n`+
            `GOAL: ${profile.targets?.goal||profile.goal||"Not set"}\n`+
            `TODAY: ${dayName}\n\n`+
            `EQUIPMENT: Full gym assumed (barbell, dumbbells, cables, machines)\n`+
            `TRAINING_FREQUENCY: ${weekSess.length} sessions in the last 7 days\n\n`+
            `RPE_CONTEXT: RPE (Rate of Perceived Exertion) 1-10 scale. RPE 6-7 = comfortable. RPE 8-9 = hard, near limit. RPE 10 = max effort, needs recovery. Use RPE data to gauge readiness — if recent sessions show RPE 9-10 on a muscle group, that group needs more recovery time.\n\n`+
            "Suggest my next training session."
          },
          { role: "user", content: `FINAL REMINDER: Stay in character for ${persona} voice. Every recommendation must reference specific recovery data or RPE numbers. PLAIN_ENGLISH_LAW: No science jargon — banned: leucine, MPS, glycogen, substrate, anabolic, catabolic, thermogenesis. Now write the JSON.` }
        ],
      });
      const cleaned = cleanAIResponse(raw);
      const obj = JSON.parse(cleaned);
      if (!obj.focus || !obj.exercises) throw new Error("bad shape");
      // Normalise arrays — LLM may return non-array values
      if (!Array.isArray(obj.exercises)) obj.exercises = [];
      if (!Array.isArray(obj.warnings)) obj.warnings = [];
      if (!Array.isArray(obj.tips)) obj.tips = [];
      obj.exercises = obj.exercises.map(ex => ({
        ...ex,
        sets: typeof ex.sets === "number" ? ex.sets : (parseInt(ex.sets) || 3),
      }));
      if (process.env.NODE_ENV !== "production") { console.log('suggestNextWorkout prompt focus:', focusOverride, durationOverride); }
      setAiSuggestion(obj);
      setShowSuggestionModal(true);
      try { localStorage.setItem("lf_last_suggestion", JSON.stringify(obj)); } catch(_){}
    } catch(e) {
      setAiError("Could not generate suggestion. Try again.");
      if (process.env.NODE_ENV !== "production") console.warn("[suggestNextWorkout]", e);
    }
    setAiLoading(false);
  }

  async function generateWorkoutSummary(routine) {
    const key = `routine_${routine.id}`;
    if (workoutSummaries[key]) return;
    setSummaryLoading(true);
    try {
      const exNames = (routine.exercises || [])
        .map(ex => {
          const libEx = library.find(l => l.id === ex.exerciseId);
          return libEx?.name || ex.name;
        })
        .filter(Boolean)
        .join(', ');

      const raw = await llmFetch({
        model: LLM_MODEL_LIGHT,
        max_tokens: 80,
        temperature: 0.3,
        messages: [
          { role: 'system', content:
            'You are a concise fitness coach. Write exactly ' +
            '1 sentence (max 20 words) describing what this ' +
            'workout trains and why. Plain English only. ' +
            'No jargon. Start with the muscle focus.' +
            AI_SAFETY_LAW + ' ' + INJURY_LAW
          },
          { role: 'user', content:
            `Workout: ${routine.name}\nExercises: ${exNames}\n` +
            'Describe in 1 sentence.'
          }
        ]
      });
      const summary = cleanAIResponse(raw).trim();
      const updated = { ...workoutSummaries, [key]: summary };
      setWorkoutSummaries(updated);
      try { localStorage.setItem('lf_workout_summaries', JSON.stringify(updated)); } catch(_){}
    } catch(e) {
      if (process.env.NODE_ENV !== 'production') console.warn('[generateWorkoutSummary]', e);
      setSummaryFailed(prev => ({...prev, [key]: true}));
    }
    setSummaryLoading(false);
  }

  const weekVol=weekSess.reduce((a,s)=>{
    for(const ex of (s.exercises||[])) for(const st of (ex.sets||[])) if(st.done) a+=(st.weight||0)*(st.reps||0);
    return a;
  },0);
  const muscleCnt={};
  for(const s of weekSess) for(const ex of (s.exercises||[])){
    const lx=library.find(e=>e.id===ex.exerciseId);
    if(lx) for(const m of (lx.muscles||[])) muscleCnt[m]=(muscleCnt[m]||0)+1;
  }
  const topMuscle=Object.entries(muscleCnt).sort((a,b)=>b[1]-a[1])[0];
  const activeProg=programmes.find(p=>p.active);

  return(
    <div style={{background:T.pageBg}}>
      <AppHeader
        title="Today"
        subtitle={new Date().toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long'})}
        persona={persona}
        darkMode={darkMode}
        onToggleDarkMode={()=>setDarkMode(d=>!d)}
        currentApp="lift"
        username={profile?.username}
      />
      <div style={{padding:"10px 16px 8px"}}>

        {/* 2. Stat tiles (Sessions + PRs) — arc progress + 4-week bars */}
        {(()=>{
          const sessTarget = 4;
          const sessPct = Math.min(weekSess.length / sessTarget, 1);

          // 4-week session history
          const weekBuckets = Array.from({length:4},(_,i)=>{
            const wEnd = new Date(); wEnd.setDate(wEnd.getDate() - i*7);
            const wStart = new Date(wEnd); wStart.setDate(wStart.getDate()-7);
            const sISO = wStart.toISOString().slice(0,10);
            const eISO = wEnd.toISOString().slice(0,10);
            return sessions.filter(s => s.date > sISO && s.date <= eISO).length;
          }).reverse();
          const maxWB = Math.max(...weekBuckets, 1);

          // PRs this week: exercises where any set weight > max previous weight for that exerciseId
          const sevenAgoISO = new Date(new Date().setDate(new Date().getDate()-7)).toISOString().slice(0,10);
          const thisWeekSess = sessions.filter(s => s.date >= sevenAgoISO);
          const olderSess = sessions.filter(s => s.date < sevenAgoISO);
          // Build max weight map from older sessions
          const maxWeightMap = {};
          for (const s of olderSess) for (const ex of (s.exercises||[])) for (const st of (ex.sets||[])) {
            if (st.done && st.weight > 0) maxWeightMap[ex.exerciseId] = Math.max(maxWeightMap[ex.exerciseId]||0, st.weight);
          }
          // Count PRs in this week's sessions
          const prExercises = new Set();
          for (const s of thisWeekSess) for (const ex of (s.exercises||[])) for (const st of (ex.sets||[])) {
            if (st.done && st.weight > 0 && st.weight > (maxWeightMap[ex.exerciseId]||0)) prExercises.add(ex.exerciseId);
          }
          const prCount = prExercises.size;
          const prTarget = 5;
          const prPct = Math.min(prCount / prTarget, 1);

          // 4-week PR history
          const prBuckets = Array.from({length:4},(_,wi)=>{
            const wEnd = new Date(); wEnd.setDate(wEnd.getDate() - wi*7);
            const wStart = new Date(wEnd); wStart.setDate(wStart.getDate()-7);
            const sISO = wStart.toISOString().slice(0,10);
            const eISO = wEnd.toISOString().slice(0,10);
            const wSess = sessions.filter(s => s.date > sISO && s.date <= eISO);
            const older = sessions.filter(s => s.date <= sISO);
            const oMap = {};
            for (const s of older) for (const ex of (s.exercises||[])) for (const st of (ex.sets||[])) {
              if (st.done && st.weight > 0) oMap[ex.exerciseId] = Math.max(oMap[ex.exerciseId]||0, st.weight);
            }
            const prs = new Set();
            for (const s of wSess) for (const ex of (s.exercises||[])) for (const st of (ex.sets||[])) {
              if (st.done && st.weight > 0 && st.weight > (oMap[ex.exerciseId]||0)) prs.add(ex.exerciseId);
            }
            return prs.size;
          }).reverse();
          const maxPB = Math.max(...prBuckets, 1);

          // Arc SVG helper — strokeDasharray circle, number hero left, compact arc right, bars below
          const ArcTile = ({value, target, pct: rawPct, label, sublabel, color, bars, maxBar}) => {
            const ratio = (!target || target <= 0) ? 0 : Math.min(Math.max(rawPct, 0), 1);
            const r = 21, cx = 27, cy = 27, sw = 4.5;
            const C = 2 * Math.PI * r; // ~131.9
            return (
              <div style={{background:T.tileBg,borderRadius:18,padding:"14px 16px 12px",minHeight:120}}>
                <div style={{fontSize:10,fontWeight:600,color:T.tileMuted,fontFamily:T.fontDisplay,marginBottom:6}}>{label}</div>
                {/* Hero row: number left, arc right */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                  <div>
                    <div style={{fontSize:40,fontWeight:800,color:T.tileText,letterSpacing:"-1px",lineHeight:1,fontFamily:T.fontDisplay}}>{value}</div>
                    <div style={{fontSize:10,fontWeight:500,color:T.tileMuted,fontFamily:T.fontDisplay,marginTop:3}}>{sublabel}</div>
                  </div>
                  {ratio > 0 && (
                    <svg viewBox="0 0 54 54" style={{width:54,height:54,flexShrink:0}}>
                      {/* Background track */}
                      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={sw}
                        strokeDasharray={`${C * 0.75} ${C * 0.25}`} strokeDashoffset={C * 0.125} strokeLinecap="round"
                        transform={`rotate(0 ${cx} ${cy})`}/>
                      {/* Value fill */}
                      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw}
                        strokeDasharray={`${C * 0.75 * ratio} ${C}`} strokeDashoffset={C * 0.125} strokeLinecap="round"
                        transform={`rotate(0 ${cx} ${cy})`}/>
                    </svg>
                  )}
                </div>
                {/* 4-week comparison */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:8}}>
                  <span style={{fontFamily:T.fontSans,fontSize:11,color:T.inkFaint}}>4w ago: {bars[0]}</span>
                  <span style={{fontFamily:T.fontSans,fontSize:11,color:T.tileAccent,fontWeight:700}}>↑ This wk: {bars[bars.length-1]}</span>
                </div>
              </div>
            );
          };

          return(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16}}>
              <ArcTile value={weekSess.length} target={sessTarget} pct={sessPct} label="Sessions this week" sublabel={`of ${sessTarget} target`} color={T.tileAccent} bars={weekBuckets} maxBar={maxWB}/>
              <ArcTile value={prCount} target={prTarget} pct={prPct} label="PRs this week" sublabel={prCount===1?"personal record":"personal records"} color={T.orange} bars={prBuckets} maxBar={maxPB}/>
            </div>
          );
        })()}

        {/* 3. Start New Workout button */}
        <button onClick={()=>onStartWorkout(null)} style={{
          width:"100%",background:T.orange,border:"none",
          borderRadius:T.radius,padding:16,
          fontFamily:T.fontDisplay,fontSize:17,fontWeight:800,
          color:T.tileText,cursor:"pointer",marginBottom:12,
          letterSpacing:"0.3px",
          boxShadow:`0 6px 24px ${T.orange}59`,
          display:"flex",alignItems:"center",justifyContent:"center",
          gap:10,
        }}>
          <div style={{width:18,height:18,borderRadius:"50%",background:"rgba(255,255,255,0.25)",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
          </div>
          Start new workout
        </button>

        {/* 4a. Unified programme card (banner + featured merged) */}
        {activeProg && (()=>{
          const weeks = activeProg.weeks || [];

          // Calculate position using completed sessions
          const progRoutineIds = weeks
            .flatMap(w => (w.days||[]).map(d => d.routineId))
            .filter(Boolean);
          const progRoutineNames = progRoutineIds.map(id => {
            const r = routines.find(r => r.id === id);
            return r?.name?.toLowerCase().trim();
          }).filter(Boolean);

          if (process.env.NODE_ENV !== 'production') {
            console.log('[PROG DEBUG] activeProg:', activeProg?.name);
            console.log('[PROG DEBUG] progRoutineIds:', progRoutineIds);
            console.log('[PROG DEBUG] progRoutineNames:', progRoutineNames);
            console.log('[PROG DEBUG] all sessions names:', sessions.map(s => s.name?.toLowerCase().trim()));
          }

          const completedProgSessions = sessions.filter(s => {
            const sName = s.name?.toLowerCase().trim() || '';
            return progRoutineNames.some(rName =>
              rName && (sName === rName ||
                sName.includes(rName) || rName.includes(sName))
            );
          }).length;

          let sessionCounter = 0;
          let targetWeek = 0;
          let targetDay = 0;
          let found = false;
          for (let wi = 0; wi < weeks.length && !found; wi++) {
            const days = weeks[wi].days || [];
            for (let di = 0; di < days.length && !found; di++) {
              if (days[di].routineId) {
                if (sessionCounter >= completedProgSessions) {
                  targetWeek = wi; targetDay = di; found = true;
                }
                sessionCounter++;
              } else {
                if (sessionCounter >= completedProgSessions && !found) {
                  targetWeek = wi; targetDay = di; found = true;
                }
              }
            }
          }

          const week = targetWeek;
          const day = targetDay;
          const todayRoutineId = weeks[week]?.days[day]?.routineId;
          const todayRoutine = routines.find(r => r.id === todayRoutineId);
          const isRestDay = !todayRoutineId;
          const totalSessions = progRoutineIds.length;
          const progressPct = totalSessions > 0
            ? Math.round((completedProgSessions / totalSessions) * 100)
            : 0;

          if (process.env.NODE_ENV !== 'production') {
            console.log('[PROG DEBUG] completedProgSessions:', completedProgSessions);
            console.log('[PROG DEBUG] targetWeek:', targetWeek, 'targetDay:', targetDay);
            console.log('[PROG DEBUG] todayRoutineId:', todayRoutineId);
            console.log('[PROG DEBUG] todayRoutine:', todayRoutine?.name);
          }

          return (
            <div style={{
              background: T.progCardBg,
              borderRadius: 18,
              marginBottom: 14,
              overflow: "hidden",
            }}>
              {/* Banner top — programme overview */}
              <div style={{
                background: T.tileBg,
                padding: "12px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
              }}>
                <div>
                  <div style={{
                    fontFamily: T.fontSans,
                    fontSize: 11, fontWeight: 700, letterSpacing: "0.5px",
                    color: T.tileAccent,
                    marginBottom: 3,
                  }}>Active programme</div>
                  <div style={{
                    fontSize: 11, fontWeight: 600, color: T.tileMuted,
                    fontFamily: T.fontSans,
                  }}>{activeProg.name}</div>
                </div>
                <div style={{
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: 8, padding: "4px 8px",
                  fontSize: 10, fontWeight: 700, color: T.tileText,
                  fontFamily: T.fontDisplay,
                }}>
                  {completedProgSessions} / {totalSessions}
                </div>
              </div>

              {/* Progress bar */}
              <div style={{
                height: 3,
                borderRadius: 2,
                backgroundColor: "rgba(255,255,255,0.10)",
                overflow: "hidden",
              }}>
                <div style={{
                  height: "100%",
                  borderRadius: 2,
                  backgroundColor: T.tileAccent,
                  width: completedProgSessions===0 ? "2px" : `${Math.min(100, Math.round((completedProgSessions/totalSessions)*100))}%`,
                }}/>
              </div>

              {/* Body — today's session or rest day */}
              <div style={{ padding: 16 }}>
                {!isRestDay && todayRoutine ? (
                  <>
                    {/* Today label */}
                    <div style={{
                      fontFamily: T.fontDisplay,
                      fontSize: 10, fontWeight: 600, letterSpacing: "1px",
                      color: "rgba(255,255,255,0.30)",
                      marginBottom: 6,
                    }}>
                      Today
                    </div>

                    {/* Routine name — title */}
                    <div style={{
                      display: "flex", alignItems: "center",
                      justifyContent: "space-between", marginBottom: 10,
                    }}>
                      <div>
                        {(()=>{
                          const displayName = todayRoutine.name
                            .replace(/\s*[—–-]+\s*WK\s*\d+.*/i, '')
                            .replace(/\s*[—–-]+\s*WEEK\s*\d+.*/i, '')
                            .trim();
                          return (
                            <div style={{
                              fontFamily: T.fontDisplay,
                              fontSize: 42, fontWeight: 800, color: T.tileText,
                              letterSpacing: "-1px", lineHeight: 1,
                              textTransform: "uppercase",
                            }}>
                              {displayName}
                            </div>
                          );
                        })()}
                        <div style={{
                          fontSize: 13, color: T.inkMuted, marginTop: 4,
                          fontFamily: T.fontSans,
                        }}>
                          {(todayRoutine.exercises || []).length} exercises · ~{workoutDuration} mins
                        </div>
                      </div>
                      <div
                        onClick={() => onStartRoutine(todayRoutine)}
                        style={{
                          width: 42, height: 42, borderRadius: "50%",
                          background: "rgba(255,255,255,0.10)", display: "flex",
                          alignItems: "center", justifyContent: "center",
                          flexShrink: 0, cursor: "pointer",
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="rgba(255,255,255,0.5)">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    </div>

                    {/* AI workout summary */}
                    {(()=>{
                      const summaryKey = `routine_${todayRoutine.id}`;
                      const summary = workoutSummaries[summaryKey];
                      if (!summary && !summaryLoading && !summaryFailed[summaryKey]) {
                        generateWorkoutSummary(todayRoutine);
                      }
                      return (
                        <div style={{ minHeight: 36, marginBottom: 14 }}>
                          {summary ? (
                            <p style={{
                              fontSize: 12, color: "rgba(255,255,255,0.50)",
                              fontFamily: T.fontDisplay, lineHeight: 1.55,
                              margin: 0,
                            }}>{summary}</p>
                          ) : summaryFailed[summaryKey] ? (
                            <p style={{
                              fontSize: 11, color: "rgba(255,255,255,0.25)",
                              fontFamily: T.fontDisplay, margin: 0,
                            }}>Ready to start</p>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Spinner size={12}/>
                              <span style={{
                                fontSize: 11, color: "rgba(255,255,255,0.35)",
                                fontFamily: T.fontDisplay,
                              }}>Analysing workout…</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Divider */}
                    <div style={{height:1,background:"rgba(255,255,255,0.07)"}}/>

                    {/* Action buttons */}
                    <div style={{ display: "flex", gap: 8, paddingTop: 14 }}>
                      <button
                        onClick={() => onStartRoutine(todayRoutine)}
                        style={{
                          flex: 1, background: T.orange, border: "none",
                          borderRadius: 12, padding: "12px 16px",
                          fontFamily: T.fontDisplay, fontSize: 12,
                          fontWeight: 700, color: T.tileText, cursor: "pointer",
                        }}
                      >
                        Start today's session
                      </button>
                      <button
                        onClick={() => activeProg ? setShowSwitchModal(true) : onStartWorkout(null)}
                        style={{
                          flex: 0, background: "rgba(255,255,255,0.07)", border: "none",
                          borderRadius: 12, padding: "12px 16px",
                          fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.45)",
                          fontFamily: T.fontDisplay, cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Switch session
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Rest day */}
                    <div style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: "1px",
                      color: "rgba(255,255,255,0.35)", textTransform: "uppercase",
                      fontFamily: T.fontDisplay, marginBottom: 6,
                    }}>
                      Week {week + 1} · Day {day + 1}
                    </div>
                    <div style={{
                      fontFamily: T.fontDisplay, fontSize: 28,
                      fontWeight: 800, color: T.tileText, marginBottom: 4,
                    }}>
                      Rest day
                    </div>
                    <div style={{
                      fontSize: 11, color: "rgba(255,255,255,0.35)",
                      fontFamily: T.fontDisplay, marginBottom: 14,
                    }}>
                      Recovery day — your muscles are rebuilding
                    </div>
                    <button
                      onClick={() => onStartWorkout(null)}
                      style={{
                        width: "100%", background: "rgba(255,255,255,0.07)", border: "none",
                        borderRadius: 12, padding: 13,
                        fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.45)",
                        fontFamily: T.fontDisplay, cursor: "pointer",
                      }}
                    >
                      Start a workout anyway
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })()}

        {/* 4b. Next Up routine card (no programme + no AI suggestion yet) */}
        {!activeProg && !aiSuggestion && !aiLoading && routines.length > 0 && (()=>{
          const lastSession = [...sessions]
            .sort((a,b)=>b.date<a.date?-1:1)[0];
          const lastMatchIdx = lastSession
            ? routines.findIndex(r=>
                r.name?.toLowerCase().trim()===
                lastSession.name?.toLowerCase().trim())
            : -1;
          const nextRoutine = lastMatchIdx >= 0
            ? routines[(lastMatchIdx+1)%routines.length]
            : routines[0];
          if(!nextRoutine) return null;
          return(
            <div style={{
              background:T.progCardBg,borderRadius:18,
              padding:16,marginBottom:14,
            }}>
              <div style={{
                fontSize:10,fontWeight:700,letterSpacing:"1.5px",
                color:T.progEyebrow,textTransform:"uppercase",
                fontFamily:T.fontDisplay,marginBottom:6,
              }}>Next up</div>
              <div style={{
                display:"flex",alignItems:"center",
                justifyContent:"space-between",marginBottom:10,
              }}>
                <div>
                  <div style={{
                    fontFamily:T.fontDisplay,fontSize:38,
                    fontWeight:800,color:T.tileText,
                    letterSpacing:"-1px",lineHeight:1,
                    textTransform:"uppercase",
                  }}>{nextRoutine.name}</div>
                  <div style={{
                    fontSize:11,color:"rgba(255,255,255,0.35)",marginTop:4,
                    fontFamily:T.fontDisplay,
                  }}>
                    {(nextRoutine.exercises||[]).length} exercises
                  </div>
                </div>
                <div onClick={()=>onStartRoutine(nextRoutine)}
                  style={{
                    width:42,height:42,borderRadius:"50%",
                    background:"rgba(255,255,255,0.10)",display:"flex",
                    alignItems:"center",justifyContent:"center",
                    flexShrink:0,cursor:"pointer",
                  }}>
                  <svg width="16" height="16"
                    viewBox="0 0 24 24" fill="rgba(255,255,255,0.5)">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
              </div>
              <div style={{height:1,background:"rgba(255,255,255,0.07)",marginBottom:12}}/>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>onStartRoutine(nextRoutine)}
                  style={{
                    flex:1,background:T.orange,border:"none",
                    borderRadius:12,padding:"12px 16px",
                    fontFamily:T.fontDisplay,fontSize:12,
                    fontWeight:700,color:T.tileText,cursor:"pointer",
                  }}>
                  Start this workout
                </button>
                <button onClick={()=>activeProg ? setShowSwitchModal(true) : onStartWorkout(null)}
                  style={{
                    flex:0,background:"rgba(255,255,255,0.07)",border:"none",
                    borderRadius:12,padding:"12px 16px",
                    fontSize:12,fontWeight:600,color:"rgba(255,255,255,0.45)",
                    fontFamily:T.fontDisplay,cursor:"pointer",
                    whiteSpace:"nowrap",
                  }}>
                  Switch session
                </button>
              </div>
            </div>
          );
        })()}

        {/* 4c. AI suggestion card (always shows when sessions >= 3) */}
        {(()=>{
          const hasActiveProg = !!activeProg;
          if (aiSuggestion) {
            return (
              <div style={{
                background:T.card,
                borderRadius:18,
                marginBottom:16,
                border:`1px solid ${T.cardBorder}`,
                overflow:"hidden",
              }}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",backgroundColor:T.tileBg,borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
                  <div style={{
                    fontFamily:T.fontSans,
                    fontSize:11,fontWeight:700,letterSpacing:"0.5px",
                    color:T.actionPrimary,
                  }}>AI-generated workout</div>
                  <div style={{
                    backgroundColor:"rgba(249,115,22,0.2)",color:T.actionPrimary,
                    fontSize:10,fontWeight:700,borderRadius:8,
                    padding:"2px 8px",fontFamily:T.fontSans,
                  }}>AI</div>
                </div>
                <div style={{padding:16}}>
                <div style={{
                  display:"flex",alignItems:"center",
                  justifyContent:"space-between",marginBottom:10,
                }}>
                  <div>
                    <div style={{
                      fontFamily:T.fontDisplay,
                      fontSize:42,
                      fontWeight:800,color:T.inkDark,
                      letterSpacing:"-1px",lineHeight:1,
                      textTransform:"uppercase",
                    }}>{aiSuggestion.focus}</div>
                    <div style={{
                      fontSize:11,color:T.inkMuted,marginTop:4,
                      fontFamily:T.fontDisplay,fontWeight:500,
                    }}>
                      {workoutFocus
                        ? workoutFocus.charAt(0).toUpperCase()+workoutFocus.slice(1)
                        : "AI selected"} · {workoutDuration} mins
                    </div>
                  </div>
                  <div style={{
                    width:46,height:46,borderRadius:"50%",
                    background:T.orange,display:"flex",
                    alignItems:"center",justifyContent:"center",
                    flexShrink:0,cursor:"pointer",
                    boxShadow:`0 4px 16px ${T.orange}59`,
                  }} onClick={()=>setShowSuggestionModal(true)}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill={T.tileText}>
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                </div>
                {aiLoading && (
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <Spinner size={13}/>
                    <span style={{fontSize:11,color:T.inkMuted,fontFamily:T.fontDisplay}}>
                      Analysing your training history…
                    </span>
                  </div>
                )}
                {aiError && (
                  <p style={{color:T.danger,fontSize:11,fontFamily:T.fontDisplay,margin:0}}>
                    {aiError}
                  </p>
                )}
                {aiSuggestion && !aiLoading && (
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    <button onClick={()=>setShowSuggestionModal(true)} style={{
                      background:T.tileBg,border:"none",borderRadius:20,padding:"8px 14px",
                      fontSize:12,fontWeight:600,color:T.tileText,cursor:"pointer",fontFamily:T.fontDisplay,
                      display:"flex",alignItems:"center",gap:5,
                    }}>
                      <Play size={12} weight="fill"/> Preview
                    </button>
                    <button onClick={()=>suggestNextWorkout(workoutFocus,workoutDuration)} disabled={aiLoading} style={{
                      background:T.orange,border:"none",borderRadius:20,padding:"8px 14px",
                      fontSize:12,fontWeight:600,color:T.tileText,cursor:"pointer",fontFamily:T.fontDisplay,
                      opacity:aiLoading?0.5:1,
                    }}>
                      ↺ Regenerate
                    </button>
                    <button onClick={()=>setShowWorkoutModal(true)} style={{
                      background:"transparent",border:`1px solid ${T.chipBorder}`,borderRadius:20,padding:"8px 14px",
                      fontSize:12,fontWeight:600,color:T.chipText,cursor:"pointer",fontFamily:T.fontDisplay,
                    }}>
                      Change muscle group
                    </button>
                  </div>
                )}
                <p style={{
                  margin:"10px 0 0",fontSize:10,fontFamily:T.fontDisplay,
                  color:T.disclaimerText,fontStyle:"italic",lineHeight:1.5,
                }}>
                  AI suggestions are based on logged data only.
                  Always listen to your body.
                </p>
                </div>{/* end body padding div */}
              </div>
            );
          }
          // No suggestion yet — show generate button
          if (sessions.length >= 3) {
            return (
              <div style={{
                background:T.card,borderRadius:18,
                marginBottom:16,border:`1px solid ${T.cardBorder}`,
                overflow:"hidden",
              }}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",backgroundColor:T.tileBg,borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
                  <div style={{
                    fontFamily:T.fontSans,
                    fontSize:11,fontWeight:700,letterSpacing:"0.5px",
                    color:T.actionPrimary,
                  }}>AI-generated workout</div>
                  <div style={{
                    backgroundColor:"rgba(249,115,22,0.2)",color:T.actionPrimary,
                    fontSize:10,fontWeight:700,borderRadius:8,
                    padding:"2px 8px",fontFamily:T.fontSans,
                  }}>AI</div>
                </div>
                <div style={{padding:16}}>
                {aiLoading ? (
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <Spinner size={13}/>
                    <span style={{fontSize:11,color:T.inkMuted,fontFamily:T.fontDisplay}}>
                      Analysing your training history…
                    </span>
                  </div>
                ) : (
                  <Btn small onClick={()=>setShowWorkoutModal(true)} color={T.aiColor}>
                    <Lightning size={14} weight="fill"/> AI Suggest Workout
                  </Btn>
                )}
                {aiError && <p style={{color:T.danger,fontSize:11,fontFamily:T.fontDisplay,marginTop:6}}>{aiError}</p>}
                </div>{/* end body padding div */}
              </div>
            );
          }
          return null;
        })()}

        {/* 5a. Ask about training — expanded card with suggestion chips */}
        {(()=>{
          const pillColor = workoutChatCap.remaining <= 3 ? (T.red || T.danger) : T.orange;
          const pillText = workoutChatCap.remaining === 0 ? "No messages left" : `${workoutChatCap.remaining} left`;
          const canSend = !workoutChatCap.isExhausted && !workoutChatLoading && chatInput.trim();
          const doSend = () => { if (!canSend) return; handleWorkoutChatSend(chatInput.trim()); setChatInput(""); };
          const chipSend = (text) => { if (!workoutChatCap.isExhausted && !workoutChatLoading) handleWorkoutChatSend(text); };
          const suggestions = ["Am I overtraining?", "Best recovery tips", "Help with plateau"];
          return (
            <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,marginBottom:12,overflow:"hidden",padding:16}}>
              {/* Header */}
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                <div style={{width:28,height:28,borderRadius:8,background:T.surfaceLight,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <span style={{fontSize:14}}>💬</span>
                </div>
                <span style={{color:T.inkDark,fontSize:14,fontWeight:700,fontFamily:T.fontDisplay,flex:1}}>Ask about training</span>
                <span style={{background:pillColor,color:T.tileText,fontSize:9,fontWeight:700,borderRadius:8,padding:"3px 7px",fontFamily:T.fontDisplay}}>{pillText}</span>
              </div>

              {/* Suggestion chips — only show when no messages yet */}
              {workoutChatHistory.length === 0 && !workoutChatCap.isExhausted && (
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:12}}>
                  {suggestions.map(s=>(
                    <button key={s} onClick={()=>chipSend(s)} style={{
                      background:T.surfaceLight,border:`1px solid ${T.surfaceMid}`,borderRadius:20,
                      padding:"7px 12px",fontSize:11,fontWeight:500,color:T.chipText,
                      cursor:"pointer",fontFamily:T.fontDisplay,
                    }}>{s}</button>
                  ))}
                </div>
              )}

              {/* Messages */}
              {workoutChatHistory.length > 0 && (
                <div ref={chatMsgsRef} style={{display:"flex",flexDirection:"column",gap:8,maxHeight:300,overflowY:"auto",marginBottom:10}}>
                  {workoutChatHistory.map((msg,i)=>(
                    <div key={i} style={{
                      alignSelf:msg.role==="user"?"flex-end":"flex-start",maxWidth:"85%",
                      background:msg.role==="user"?T.orange:T.surfaceLight,
                      color:msg.role==="user"?T.tileText:T.inkDark,
                      borderRadius:msg.role==="user"?"14px 14px 4px 14px":"14px 14px 14px 4px",
                      padding:"9px 12px",fontSize:12,lineHeight:1.5,fontFamily:T.fontDisplay,
                    }}>
                      {msg.role==="assistant"?<AIText text={msg.content}/>:msg.content}
                    </div>
                  ))}
                  {workoutChatLoading && (
                    <div style={{alignSelf:"flex-start",maxWidth:"85%",background:T.surfaceLight,borderRadius:"14px 14px 14px 4px",padding:"9px 12px"}}>
                      <span style={{color:T.inkMuted,fontSize:12,fontFamily:T.fontDisplay}}>Thinking...</span>
                    </div>
                  )}
                  {workoutChatError && (
                    <div style={{alignSelf:"flex-start",maxWidth:"85%",background:`${T.danger}18`,borderRadius:"14px 14px 14px 4px",padding:"9px 12px"}}>
                      <span style={{color:T.danger,fontSize:12,fontFamily:T.fontDisplay}}>{String(workoutChatError)}</span>
                    </div>
                  )}
                </div>
              )}
              {workoutChatLoading && workoutChatHistory.length === 0 && (
                <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:10}}>
                  <Spinner size={13}/>
                  <span style={{color:T.inkMuted,fontSize:12,fontFamily:T.fontDisplay}}>Thinking...</span>
                </div>
              )}

              {workoutChatCap.isExhausted ? (
                <div style={{background:T.surfaceLight,borderRadius:12,padding:"10px 14px",textAlign:"center"}}>
                  <span style={{color:T.inkMuted,fontSize:11,fontWeight:600,fontFamily:T.fontDisplay}}>No messages left today. Resets at midnight.</span>
                </div>
              ) : (
                <>
                  {/* Input row */}
                  <div style={{display:"flex",gap:8}}>
                    <input
                      value={chatInput} onChange={e=>setChatInput(e.target.value)}
                      onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();doSend();}}}
                      placeholder="Ask about your training..."
                      style={{
                        flex:1,padding:"10px 14px",borderRadius:12,
                        border:`1px solid ${T.surfaceMid}`,
                        fontSize:12,background:T.surfaceLight,color:T.inkDark,outline:"none",
                        fontFamily:T.fontDisplay,
                      }}
                    />
                    <button onClick={doSend} disabled={!canSend} style={{
                      width:42,height:42,borderRadius:12,border:"none",cursor:canSend?"pointer":"default",
                      background:T.orange,opacity:canSend?1:0.35,
                      display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,
                    }}>
                      <PaperPlaneTilt size={16} weight="fill" color={T.tileText}/>
                    </button>
                  </div>
                  <p style={{fontSize:9,color:T.inkFaint,margin:"8px 0 0",fontFamily:T.fontDisplay}}>Training & recovery only · not medical advice</p>
                </>
              )}
            </div>
          );
        })()}

        {/* 5b. "More" collapsible section */}
        <div style={{marginBottom:8}}>
          <button onClick={()=>setMoreOpen(o=>!o)} style={{
            width:"100%",display:"flex",alignItems:"center",
            justifyContent:"space-between",background:T.card,
            border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusSm,padding:"14px 16px",
            cursor:"pointer",minHeight:44,
          }}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <div style={{width:28,height:28,borderRadius:8,background:T.surfaceLight,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <span style={{fontSize:14}}>📊</span>
              </div>
              <span style={{
                fontSize:13,fontWeight:600,color:T.inkDark,
                fontFamily:T.fontDisplay,
              }}>More</span>
            </div>
            <span style={{color:T.inkFaint,fontSize:14}}>›</span>
          </button>
          {moreOpen && (
            <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:0}}>

              {/* Today's sessions if any */}
              {todaySessions.length > 0 && (
                <ComicCard>
                  <SectionLabel>Today</SectionLabel>
                  {todaySessions.map(s=>(
                    <div key={s.id} style={{
                      display:"flex",justifyContent:"space-between",
                      alignItems:"center",marginBottom:6,paddingBottom:6,
                      borderBottom:`1px solid ${T.surface}`,
                    }}>
                      <div>
                        <p style={{
                          margin:0,fontSize:13,fontFamily:T.fontSans,
                          fontWeight:900,color:T.text,
                        }}>{s.name||"Workout"}</p>
                        <p style={{
                          margin:"2px 0 0",fontSize:10,
                          fontFamily:T.fontSans,color:T.textDim,
                        }}>
                          {completedSets(s)} sets &bull; {totalVolume(s,unit)} &bull; {fmtDuration(s.duration||0)}
                        </p>
                      </div>
                      <Check size={16} weight="bold" color={T.accent}/>
                    </div>
                  ))}
                </ComicCard>
              )}

              {/* Streak cards */}
              {(()=>{
                const {current,best}=calcWorkoutStreak(sessions);
                if(!current) return null;
                return(
                  <div style={{
                    display:"grid",gridTemplateColumns:"1fr 1fr",
                    gap:10,marginBottom:16,
                  }}>
                    <div style={{
                      background:T.card,boxShadow:T.shadowSm,
                      borderRadius:T.radiusSm,padding:"14px 16px",
                      textAlign:"center",
                    }}>
                      <p style={{
                        margin:"0 0 2px",fontSize:9,fontFamily:T.fontSans,
                        fontWeight:900,color:T.textDim,
                        textTransform:"uppercase",letterSpacing:"0.5px",
                      }}>Training Streak</p>
                      <p style={{
                        margin:0,fontSize:24,fontFamily:T.fontSans,
                        fontWeight:900,color:T.primary,lineHeight:1,
                      }}>
                        {current}
                        <span style={{fontSize:12}}>
                          {current===1?" day":" days"}
                        </span>
                      </p>
                    </div>
                    <div style={{
                      background:T.card,boxShadow:T.shadowSm,
                      borderRadius:T.radiusSm,padding:"14px 16px",
                      textAlign:"center",
                    }}>
                      <p style={{
                        margin:"0 0 2px",fontSize:9,fontFamily:T.fontSans,
                        fontWeight:900,color:T.textDim,
                        textTransform:"uppercase",letterSpacing:"0.5px",
                      }}>Best Streak</p>
                      <p style={{
                        margin:0,fontSize:24,fontFamily:T.fontSans,
                        fontWeight:900,color:T.primary,lineHeight:1,
                      }}>
                        {best}
                        <span style={{fontSize:12}}>
                          {best===1?" day":" days"}
                        </span>
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* Deload detection */}
              {!deloadDismissed && (()=>{
                if (sessions.length < 10) return null;
                const getVol=(s)=>(s.exercises||[]).reduce((a,ex)=>
                  a+(ex.sets||[]).reduce((b,st)=>
                    b+(st.done?(st.weight||0)*(st.reps||0):0),0),0);
                const now=new Date();
                const d7=new Date(now); d7.setDate(d7.getDate()-7);
                const d28=new Date(now); d28.setDate(d28.getDate()-28);
                const d7ISO=d7.toISOString().slice(0,10);
                const d28ISO=d28.toISOString().slice(0,10);
                const recent7=sessions.filter(s=>s.date>=d7ISO);
                const prior21=sessions.filter(s=>
                  s.date>=d28ISO&&s.date<d7ISO);
                if(recent7.length===0||prior21.length===0) return null;
                const avg7=recent7.reduce((s,sess)=>
                  s+getVol(sess),0)/recent7.length;
                const avg21=prior21.reduce((s,sess)=>
                  s+getVol(sess),0)/prior21.length;
                if(avg21===0||avg7>=avg21*0.8) return null;
                return(
                  <div style={{
                    background:T.gold+"22",
                    border:`2px solid ${T.gold}`,
                    padding:"10px 13px",marginBottom:14,
                    borderRadius:T.radiusSm||12,
                    display:"flex",alignItems:"flex-start",gap:8,
                  }}>
                    <span style={{fontSize:16,flexShrink:0}}>&#x26A1;</span>
                    <div style={{flex:1}}>
                      <p style={{
                        margin:"0 0 4px",fontSize:11,
                        fontFamily:T.fontSans,fontWeight:900,
                        color:T.gold,
                      }}>Volume Drop Detected</p>
                      <p style={{
                        margin:0,fontSize:10,fontFamily:T.fontSans,
                        color:T.textSub,lineHeight:1.5,
                      }}>
                        Your training volume has dropped significantly. Consider a planned deload week — reduce weights by 40–50% and focus on form.
                      </p>
                      <button onClick={()=>setDeloadDismissed(true)}
                        style={{
                          background:"none",border:"none",
                          color:T.textDim,fontSize:9,
                          fontFamily:T.fontSans,fontWeight:900,
                          cursor:"pointer",padding:"4px 0",marginTop:4,
                          textTransform:"uppercase",letterSpacing:"0.5px",
                        }}>Dismiss</button>
                    </div>
                  </div>
                );
              })()}

              {/* Muscle Volume */}
              {sessions.length > 0 && (
                <ComicCard>
                  <MuscleVolumeChart sessions={sessions} library={library}/>
                </ComicCard>
              )}

              {/* Health Connect */}
              {hcData?.isNative && hcData?.isAvailable && hcData?.healthData?.activeCalories !== null && (
                <ComicCard style={{marginBottom:14}}>
                  <SectionLabel>Activity Today</SectionLabel>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                    {hcData.healthData.steps!==null&&(
                      <div style={{textAlign:"center"}}>
                        <p style={{margin:0,fontSize:22,fontWeight:900,fontFamily:T.fontSans,color:T.accent}}>
                          {hcData.healthData.steps.toLocaleString()}
                        </p>
                        <p style={{margin:"2px 0 0",fontSize:9,fontFamily:T.fontSans,color:T.textDim,textTransform:"uppercase"}}>Steps</p>
                      </div>
                    )}
                    <div style={{textAlign:"center"}}>
                      <p style={{margin:0,fontSize:22,fontWeight:900,fontFamily:T.fontSans,color:T.accent}}>
                        {hcData.healthData.activeCalories}
                      </p>
                      <p style={{margin:"2px 0 0",fontSize:9,fontFamily:T.fontSans,color:T.textDim,textTransform:"uppercase"}}>Active kcal</p>
                    </div>
                  </div>
                  <p style={{margin:"8px 0 0",fontSize:8,fontFamily:T.fontSans,color:T.textDim,textAlign:"right"}}>
                    Powered by Health Connect
                  </p>
                </ComicCard>
              )}

              {/* Recent sessions — collapsible */}
              {recent.length>0&&(
                <ComicCard style={{overflow:"hidden"}}>
                  <button onClick={()=>setSessionsOpen(o=>!o)} style={{
                    width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",
                    background:"none",border:"none",cursor:"pointer",padding:0,minHeight:44,
                  }}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{color:T.primary,fontSize:11,fontWeight:700,fontFamily:T.fontSans,textTransform:"uppercase"}}>Recent Sessions</span>
                      <span style={{background:T.primary,color:"#fff",fontSize:10,fontWeight:700,borderRadius:20,padding:"2px 8px",fontFamily:T.fontSans}}>{recent.length}</span>
                    </div>
                    <CaretDown size={14} color={T.textDim} style={{transform:sessionsOpen?"rotate(180deg)":"rotate(0deg)",transition:"transform 0.2s"}}/>
                  </button>
                  {sessionsOpen && (
                    <div style={{marginTop:8}}>
                      {recent.map(s=>(
                        <div key={s.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingBottom:8,marginBottom:8,borderBottom:`1px solid ${T.surface}`}}>
                          <div>
                            <p style={{margin:0,fontSize:12,fontFamily:T.fontSans,fontWeight:900,color:T.text}}>{s.name||"Workout"}</p>
                            <p style={{margin:"2px 0 0",fontSize:10,fontFamily:T.fontSans,color:T.textDim}}>{fmtDate(s.date)} &bull; {(s.exercises||[]).length} exercises &bull; {totalVolume(s,unit)}</p>
                          </div>
                          <span style={{fontSize:10,fontFamily:T.fontSans,color:T.textDim}}>{fmtDuration(s.duration||0)}</span>
                        </div>
                      ))}
                      <Btn outline small onClick={onViewHistory}>View All History</Btn>
                    </div>
                  )}
                </ComicCard>
              )}

              {/* Empty state */}
              {sessions.length===0&&(
                <div style={{background:T.card,borderRadius:T.radius,border:`1.5px solid ${T.primary||T.accent}`,padding:24,marginBottom:16,textAlign:"center"}}>
                  <span style={{fontSize:22,display:"block",marginBottom:12}}>🏋️</span>
                  <p style={{fontSize:18,fontWeight:700,color:T.ink,margin:"0 0 8px",fontFamily:T.fontSans}}>Welcome to LiftLithe</p>
                  <p style={{fontSize:14,color:T.textDim,lineHeight:"1.5",margin:"0 0 20px",fontFamily:T.fontSans}}>Track your workouts, build routines, and let AI suggest your next session based on recovery.</p>
                  <div style={{display:"flex",flexDirection:"column",gap:10}}>
                    <Btn onClick={()=>onStartWorkout(null)} color={T.primary||T.accent}>Create Your First Routine</Btn>
                    <Btn outline onClick={()=>onStartWorkout(null)}>Start a Blank Workout</Btn>
                  </div>
                </div>
              )}

            </div>
          )}
        </div>
      </div>

      {/* V28 FIX 5: AI Workout Suggestion Modal */}
      {showSuggestionModal && aiSuggestion && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:9999,display:"flex",flexDirection:"column",justifyContent:"flex-end"}}>
          <div style={{background:T.card,borderRadius:"16px 16px 0 0",maxHeight:"85vh",overflow:"auto",padding:"0 0 20px"}}>
            {/* Header */}
            <div style={{padding:"16px 16px 12px",borderBottom:T.border,display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,background:T.card,zIndex:1}}>
              <div>
                <p style={{margin:0,fontSize:14,fontFamily:T.fontSans,fontWeight:900,color:T.ink,textTransform:"uppercase",letterSpacing:"1px"}}>AI Suggested Workout</p>
                <p style={{margin:"2px 0 0",fontSize:10,fontFamily:T.fontSans,color:T.textDim}}><AIText text={aiSuggestion.rationale} persona={persona}/></p>
              </div>
              <button onClick={()=>setShowSuggestionModal(false)} style={{background:"none",border:"none",cursor:"pointer",color:T.textDim,padding:4}}>
                <X size={18} weight="bold"/>
              </button>
            </div>
            {/* Focus & Exercises */}
            <div style={{padding:"12px 16px"}}>
              <p style={{margin:"0 0 12px",fontSize:18,fontFamily:T.fontSans,fontWeight:900,color:T.accent,textTransform:"uppercase"}}>{aiSuggestion.focus}</p>
              {/* Warnings */}
              {Array.isArray(aiSuggestion.warnings)&&aiSuggestion.warnings.length>0&&(
                <div style={{background:`${T.macroCalories}22`,border:`1px solid ${T.macroCalories}`,borderRadius:T.radiusSm,padding:"8px 12px",marginBottom:12}}>
                  {aiSuggestion.warnings.map((w,i)=>(
                    <p key={i} style={{margin:i>0?"4px 0 0":0,fontSize:10,fontFamily:T.fontSans,color:T.macroCalories,fontWeight:700}}>⚠ <AIText text={w} persona={persona}/></p>
                  ))}
                </div>
              )}
              {/* Exercise list */}
              {(aiSuggestion.exercises||[]).map((ex,i)=>(
                <div key={i} style={{background:T.surface,border:T.border,borderRadius:T.radiusSm,padding:"12px",marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                    <p style={{margin:0,fontSize:13,fontFamily:T.fontSans,fontWeight:900,color:T.ink}}>{ex.name}</p>
                    <span style={{color:T.accent,fontSize:12,fontFamily:T.fontSans,fontWeight:900}}>{ex.sets} × {ex.reps}</span>
                  </div>
                  {ex.notes && <p style={{margin:0,fontSize:9,fontFamily:T.fontSans,color:T.textDim}}><AIText text={ex.notes} persona={persona}/></p>}
                </div>
              ))}
            </div>
            {/* Action buttons */}
            <div style={{padding:"0 16px",display:"flex",gap:8}}>
              <Btn color={T.accent} onClick={()=>{
                // Start workout with AI-suggested exercises
                const templateExercises = (aiSuggestion.exercises||[]).map(ex => {
                  const libEx = library.find(l => l.name.toLowerCase().includes(ex.name.toLowerCase().split(" ")[0]));
                  const repsStr = String(ex.reps||"8");
                  const reps = parseInt(repsStr) || 8;
                  return {
                    exerciseId: libEx?.id || "custom_" + ex.name.toLowerCase().replace(/\s+/g,"_"),
                    name: ex.name,
                    sets: Array.from({length: ex.sets||3}, () => ({weight:"",reps:String(reps),rpe:"",done:false,setType:"normal"})),
                    category: libEx?.category || "Other",
                    muscles: libEx?.muscles || [],
                  };
                });
                setShowSuggestionModal(false);
                onStartWorkout({exercises: templateExercises, name: aiSuggestion.focus || "AI Workout"});
              }} style={{flex:2}}>
                <Play size={16} weight="fill"/> Start This Workout
              </Btn>
              <Btn outline onClick={()=>setShowSuggestionModal(false)} style={{flex:1}}>
                Close
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* Switch session modal */}
      {showSwitchModal && activeProg && (()=>{
        const modalWeeks = activeProg.weeks || [];
        const modalRoutineIds = modalWeeks.flatMap(w=>(w.days||[]).map(d=>d.routineId)).filter(Boolean);
        const modalRoutineNames = modalRoutineIds.map(id=>{
          const r = routines.find(r=>r.id===id);
          return r?.name?.toLowerCase().trim();
        }).filter(Boolean);
        const completedCount = sessions.filter(s=>{
          const sn = s.name?.toLowerCase().trim()||'';
          return modalRoutineNames.some(rn=>rn&&(sn===rn||sn.includes(rn)||rn.includes(sn)));
        }).length;
        let tWeek=0, tDay=0, found=false, sessionCounter=0;
        for(let wi=0;wi<modalWeeks.length&&!found;wi++){
          const days=modalWeeks[wi].days||[];
          for(let di=0;di<days.length&&!found;di++){
            if(days[di].routineId){
              if(sessionCounter>=completedCount){tWeek=wi;tDay=di;found=true;}
              sessionCounter++;
            } else {
              if(sessionCounter>=completedCount&&!found){tWeek=wi;tDay=di;found=true;}
            }
          }
        }
        const weekDays = modalWeeks[tWeek]?.days || [];
        const totalWeeks = modalWeeks.length;
        const sevenAgoISO = (()=>{ const d=new Date(); d.setDate(d.getDate()-7); return d.toISOString().slice(0,10); })();
        const recentNames = sessions.filter(s=>s.date>=sevenAgoISO).map(s=>s.name?.toLowerCase().trim()||'');
        const workoutDays = weekDays.filter(d=>d.routineId);
        const allDone = workoutDays.length>0 && workoutDays.every(d=>{
          const r=routines.find(r=>r.id===d.routineId);
          if(!r) return false;
          const rn=r.name.toLowerCase().trim();
          return recentNames.some(sn=>sn===rn||sn.includes(rn)||rn.includes(sn));
        });
        return (
          <div style={{position:"fixed",inset:0,background:T.backdrop,display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:9999}} onClick={()=>setShowSwitchModal(false)}>
            <div style={{background:T.card,borderRadius:"18px 18px 0 0",border:`1.5px solid ${T.cardBorder}`,padding:20,width:"100%",maxWidth:480,maxHeight:"80vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
              {/* Header */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                <div style={{fontSize:18,fontWeight:800,color:T.ink,fontFamily:T.fontDisplay}}>This week's sessions</div>
                <button onClick={()=>setShowSwitchModal(false)} style={{background:"none",border:"none",cursor:"pointer",padding:4,lineHeight:0}}>
                  <X size={20} color={T.inkMuted}/>
                </button>
              </div>
              <div style={{fontSize:12,color:T.inkMuted,fontFamily:T.fontSans,marginBottom:18}}>
                Week {tWeek+1} of {totalWeeks} · {activeProg.name}
              </div>
              {/* All done banner */}
              {allDone && (
                <div style={{background:T.surfaceLight,borderRadius:T.radiusSm,padding:"12px 14px",marginBottom:14,textAlign:"center"}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.ink,fontFamily:T.fontSans,marginBottom:2}}>All sessions done this week — great work!</div>
                  <div style={{fontSize:11,color:T.inkMuted,fontFamily:T.fontSans}}>You can still start any session again below.</div>
                </div>
              )}
              {weekDays.length===0 ? (
                <div style={{textAlign:"center",padding:"24px 0",color:T.inkMuted,fontFamily:T.fontSans,fontSize:13}}>No sessions scheduled this week</div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {weekDays.map((day,di)=>{
                    if(!day.routineId){
                      return (
                        <div key={di} style={{padding:"12px 14px",borderRadius:T.radiusSm,background:T.surfaceLight,border:`1.5px solid ${T.cardBorder}`,opacity:0.45}}>
                          <div style={{fontSize:12,fontWeight:600,color:T.inkMuted,fontFamily:T.fontSans}}>{day.label||"Rest day"}</div>
                        </div>
                      );
                    }
                    const routine = routines.find(r=>r.id===day.routineId);
                    if(!routine) return null;
                    const isToday = di===tDay;
                    const rn = routine.name.toLowerCase().trim();
                    const isDone = recentNames.some(sn=>sn===rn||sn.includes(rn)||rn.includes(sn));
                    const displayName = day.label || routine.name.replace(/\s*[—–-]+\s*(?:Foundation|Volume|Intensity|Deload|Wk\s*\d+).*/i,'').trim();
                    const exCount = (routine.exercises||[]).length;
                    return (
                      <div key={di}
                        onClick={()=>{ onStartRoutine(routine); setShowSwitchModal(false); }}
                        style={{
                          padding:"14px 16px",borderRadius:T.radiusSm,
                          background:T.card,
                          border:`1.5px solid ${isToday?T.tileAccent:T.cardBorder}`,
                          cursor:"pointer",display:"flex",alignItems:"center",
                          justifyContent:"space-between",gap:10,
                          opacity:isDone&&!isToday?0.65:1,
                        }}
                      >
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3,flexWrap:"wrap"}}>
                            <div style={{fontSize:14,fontWeight:700,color:T.ink,fontFamily:T.fontDisplay}}>{displayName}</div>
                            {isToday&&<div style={{fontSize:9,fontWeight:700,letterSpacing:"0.8px",textTransform:"uppercase",color:T.tileAccent,background:`${T.tileAccent}22`,borderRadius:4,padding:"2px 6px",fontFamily:T.fontSans,flexShrink:0}}>Today</div>}
                            {isDone&&<Check size={13} color={T.tileAccent} weight="bold" style={{flexShrink:0}}/>}
                          </div>
                          <div style={{fontSize:11,color:T.inkMuted,fontFamily:T.fontSans}}>{exCount} exercise{exCount!==1?"s":""}</div>
                        </div>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill={T.inkFaint}>
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Cancel */}
              <button onClick={()=>setShowSwitchModal(false)} style={{
                width:"100%",marginTop:14,padding:13,borderRadius:T.radiusSm,cursor:"pointer",
                background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,
                color:T.inkMuted,fontSize:14,fontFamily:T.fontSans,fontWeight:600,
              }}>Cancel</button>
            </div>
          </div>
        );
      })()}

      {/* Pre-generation workout modal */}
      {showWorkoutModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999,padding:20}}>
          <div style={{background:T.card,borderRadius:T.radius,border:`1.5px solid ${T.primary}`,padding:20,width:"100%",maxWidth:420}}>
            <p style={{margin:"0 0 4px",fontSize:11,fontFamily:T.fontSans,fontWeight:900,color:T.textDim,letterSpacing:"0.8px",textTransform:"uppercase"}}>Generate Workout</p>
            <p style={{margin:"0 0 20px",fontSize:18,fontFamily:T.fontSans,fontWeight:900,color:T.ink}}>What are you training today?</p>

            <p style={{margin:"0 0 10px",fontSize:11,fontFamily:T.fontSans,fontWeight:900,color:T.textDim,letterSpacing:"0.8px",textTransform:"uppercase"}}>Muscle Group / Focus</p>
            <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:20}}>
              {[
                {label:"Push",value:"push"},{label:"Pull",value:"pull"},{label:"Legs",value:"legs"},
                {label:"Upper Body",value:"upper"},{label:"Full Body",value:"full"},
                {label:"Chest",value:"chest"},{label:"Back",value:"back"},{label:"Shoulders",value:"shoulders"},
                {label:"Arms",value:"arms"},{label:"Core",value:"core"},{label:"AI Decides",value:""},
              ].map(opt=>(
                <button key={opt.value} onClick={()=>setWorkoutFocus(opt.value)} style={{
                  padding:"8px 14px",borderRadius:20,cursor:"pointer",border:`1px solid ${workoutFocus===opt.value?T.primary:T.surface}`,
                  background:workoutFocus===opt.value?T.primary:T.surface,
                  color:workoutFocus===opt.value?"#fff":T.ink,
                  fontSize:13,fontFamily:T.fontSans,fontWeight:500,
                }}>{opt.label}</button>
              ))}
            </div>

            <p style={{margin:"0 0 10px",fontSize:11,fontFamily:T.fontSans,fontWeight:900,color:T.textDim,letterSpacing:"0.8px",textTransform:"uppercase"}}>Session Length</p>
            <div style={{display:"flex",gap:8,marginBottom:24}}>
              {[30,45,60,75,90].map(mins=>(
                <button key={mins} onClick={()=>setWorkoutDuration(mins)} style={{
                  flex:1,padding:"10px 0",borderRadius:T.radiusSm,cursor:"pointer",textAlign:"center",
                  border:`1px solid ${workoutDuration===mins?T.primary:T.surface}`,
                  background:workoutDuration===mins?T.primary:T.surface,
                  color:workoutDuration===mins?"#fff":T.ink,
                  fontSize:13,fontFamily:T.fontSans,fontWeight:600,
                }}>{mins}m</button>
              ))}
            </div>

            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setShowWorkoutModal(false)} style={{
                flex:1,padding:14,borderRadius:12,cursor:"pointer",textAlign:"center",
                background:T.surface,border:`0.5px solid ${T.surface}`,
                color:T.ink,fontSize:15,fontFamily:T.fontSans,fontWeight:600,
              }}>Cancel</button>
              <button onClick={()=>{setShowWorkoutModal(false);suggestNextWorkout(workoutFocus,workoutDuration);}} style={{
                flex:2,padding:14,borderRadius:12,cursor:"pointer",textAlign:"center",
                background:T.primary,border:"none",
                color:"#fff",fontSize:15,fontFamily:T.fontSans,fontWeight:700,
              }}>Generate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
